import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, Copy, Lock, ArrowRight, Gift, Wand2, CheckCircle2,
  ShoppingCart, Bot, Zap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KFN = 'Kari Future Now Creations™';

const CATEGORY_COLORS = {
  'Content Creation': 'from-orange-500 to-red-600',
  'Outreach': 'from-cyan-500 to-blue-600',
  'Sales': 'from-emerald-500 to-teal-600',
  'Research': 'from-fuchsia-500 to-purple-600',
  'Monetization': 'from-yellow-500 to-amber-600',
  'Growth': 'from-pink-500 to-rose-600',
  'Productivity': 'from-indigo-500 to-violet-600',
  'Branding': 'from-amber-500 to-orange-600',
};

const PromptVault = ({ productIdOverride, embedded = false }) => {
  const params = useParams();
  const productId = productIdOverride || params.productId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/prompt-vault/${productId}`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load vault'))
      .finally(() => setLoading(false));
  }, [productId]);

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Prompt copied — paste it into ChatGPT, Claude, or Gemini');
  };

  if (loading) {
    return (
      <div className={`${embedded ? '' : 'min-h-screen pt-24 pb-20'} bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] flex items-center justify-center`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-3" />
          <p className="text-white/70 text-sm">Generating your AI Prompt Vault with GPT-5.1...</p>
          <p className="text-white/40 text-xs mt-1">(First-time load only — cached after this)</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${embedded ? '' : 'min-h-screen pt-24 pb-20'} bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] flex items-center justify-center px-4`}>
        <Card className="bg-white/5 border border-red-500/30 max-w-md p-6 text-center">
          <p className="text-red-300">{error}</p>
          <Link to="/marketplace"><Button className="mt-4 bg-gradient-to-r from-orange-500 to-red-600 text-white">Back to Marketplace</Button></Link>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className={`${embedded ? '' : 'min-h-screen pt-24 pb-20'} bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] text-white relative overflow-hidden`}
      data-testid="prompt-vault-page"
    >
      {!embedded && (
        <>
          <div className="pointer-events-none absolute top-20 -left-24 w-96 h-96 rounded-full bg-orange-500/15 blur-[120px]" />
          <div className="pointer-events-none absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-red-600/15 blur-[140px]" />
        </>
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-semibold mb-4">
            <Gift className="w-3.5 h-3.5" /> BONUS — AI PROMPT VAULT
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight max-w-3xl mx-auto">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">{data.total_count} Ready-to-Paste AI Prompts</span>
            <br />
            <span className="text-white/90 text-xl md:text-2xl font-bold">for "{data.product_name}"</span>
          </h1>
          <p className="text-white/60 mt-3 text-sm">
            Paste any prompt into ChatGPT, Claude, or Gemini and watch the AI do the work. ⚡ {KFN}
          </p>

          {data.unlocked ? (
            <Badge className="mt-4 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-3 py-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> ALL {data.total_count} PROMPTS UNLOCKED
            </Badge>
          ) : (
            <Badge className="mt-4 bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 px-3 py-1.5">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> SHOWING PREVIEW ({data.prompts.length} of {data.total_count})
            </Badge>
          )}
        </div>

        {/* Prompts list */}
        <div className="space-y-3" data-testid="prompt-vault-list">
          {data.prompts.map((p) => {
            const accent = CATEGORY_COLORS[p.category] || 'from-orange-500 to-red-600';
            return (
              <Card
                key={p.id}
                data-testid={`vault-prompt-${p.id}`}
                className="bg-white/5 border border-white/10 backdrop-blur-md hover:border-orange-500/40 transition-all"
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] uppercase tracking-wider font-bold bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
                          #{String(p.id).padStart(2, '0')} • {p.category}
                        </span>
                        {p.best_for && (
                          <span className="text-[10px] text-white/40 px-1.5 py-0.5 rounded bg-white/5 border border-white/10">
                            Best for: {p.best_for}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-white leading-tight">{p.title}</h3>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => copyPrompt(p.prompt)}
                      data-testid={`vault-copy-${p.id}`}
                      className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold h-8 flex-shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-white/10 p-3 font-mono text-xs md:text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                    {p.prompt}
                  </div>
                  {p.expected_output && (
                    <div className="mt-2 text-xs text-white/50 italic">
                      → {p.expected_output}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Locked teaser / upsell */}
        {!data.unlocked && (
          <Card className="mt-8 bg-gradient-to-br from-orange-500/15 via-red-600/10 to-amber-500/10 border-2 border-orange-500/40 backdrop-blur-md shadow-[0_0_60px_rgba(249,115,22,0.2)]">
            <CardContent className="p-8 text-center">
              <Lock className="w-12 h-12 text-orange-400 mx-auto mb-3" />
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2">
                Unlock the Other {data.total_count - data.prompts.length} Prompts
              </h2>
              <p className="text-white/70 mb-5 max-w-xl mx-auto">
                Buy "{data.product_name}" to instantly unlock all {data.total_count} prompts. They come FREE with the guide — they're the bonus AI Prompt Vault built by {KFN}.
              </p>
              <Link to="/marketplace">
                <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold shadow-xl shadow-orange-500/30">
                  <ShoppingCart className="w-5 h-5 mr-2" /> Unlock All {data.total_count} Prompts
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {data.unlocked && (
          <Card className="mt-8 bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30">
            <CardContent className="p-6 text-center">
              <Wand2 className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">Want LIVE AI tools beyond prompts?</h3>
              <p className="text-white/70 text-sm mb-4">
                The AI Auto-Poster ($29.99/mo) does what these prompts produce — automatically. 9 superpowers, infinite usage.
              </p>
              <Link to="/ai-auto-poster">
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold">
                  <Bot className="w-4 h-4 mr-2" /> See AI Auto-Poster
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-8 text-[11px] text-orange-300/80 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Made with {KFN}
        </div>
      </div>
    </div>
  );
};

export default PromptVault;
